/**
 * «Все счета» отеля — менеджер/персонал (#338).
 *
 * Менеджер/владелец видят ВСЕ фолио тенанта (полная видимость), персонал —
 * тоже (для рум-сервиса/выезда). Слева список счетов, справа детали выбранного:
 * позиции + статусы + ДЕЙСТВИЯ персонала (добавить позицию, записать оплату,
 * сменить статус). Мутации гейтятся на бэке (staffPlus) + route-rule.
 */
import { useState } from 'react';
import { Button, Card, Input, Skeleton } from '@hsc/ui';
import { FOLIO_KIND_LABELS, FOLIO_STATUS_LABELS } from '@hsc/types';
import type { FolioLineKind, FolioSummary } from '@hsc/types';
import {
  useAddFolioLine,
  useFolioDetail,
  useFolios,
  useRecordSettlement,
  useSetFolioStatus,
} from './useBilling.js';
import { formatDateTime, formatMoney } from './format.js';

const KIND_OPTIONS: FolioLineKind[] = [
  'room_charge',
  'food',
  'beverage',
  'spa',
  'beach',
  'vendor',
  'minibar',
  'damage',
  'adjustment',
  'tax',
  'service_charge',
  'tip',
  'other',
];

const STATUS_OPTIONS = [
  'open',
  'partially_settled',
  'settled',
  'closed',
  'disputed',
  'written_off',
];

function statusTone(status: string): string {
  if (status === 'settled' || status === 'closed') return 'fnb-charge__state--done';
  if (status === 'disputed' || status === 'written_off') return 'fnb-charge__state--off';
  return 'fnb-charge__state--active';
}

/* ---- staff actions panel for the selected folio ---- */

function FolioDetailPanel({
  folioId,
  onAction,
}: {
  folioId: string;
  onAction: (a: 'line' | 'payment') => void;
}): JSX.Element {
  const { data: folio, isLoading, isError, refetch } = useFolioDetail(folioId);
  const setStatus = useSetFolioStatus(folioId);

  if (isLoading) {
    return (
      <Card data-testid="folio-detail-loading">
        <Skeleton height={20} radius={6} />
      </Card>
    );
  }
  if (isError || !folio) {
    return (
      <Card data-testid="folio-detail-error">
        <p role="alert">Не удалось загрузить счёт.</p>
        <Button variant="secondary" onClick={() => void refetch()}>
          Повторить
        </Button>
      </Card>
    );
  }

  return (
    <Card data-testid="folio-detail">
      <div className="folio-card__head">
        <h2 className="hsc-card__title">Счёт {folio.id.slice(0, 8)}</h2>
        <span
          className={`fnb-charge__state ${statusTone(folio.status)}`}
          data-testid="folio-detail-status"
        >
          {FOLIO_STATUS_LABELS[folio.status] ?? folio.status}
        </span>
      </div>

      {folio.lines.length === 0 ? (
        <p className="ta-muted">Начислений пока нет.</p>
      ) : (
        <ul className="fnb-charges" data-testid="folio-detail-lines">
          {folio.lines.map((l) => (
            <li key={l.id} className="fnb-charge">
              <div className="fnb-charge__main">
                <span
                  className="fnb-charge__title"
                  style={l.reversed ? { textDecoration: 'line-through' } : undefined}
                >
                  {FOLIO_KIND_LABELS[l.kind] ?? l.kind}: {l.description}
                </span>
                <span className="fnb-charge__meta">
                  {l.qty} × {formatMoney(l.unit_price, l.currency)} · {formatDateTime(l.posted_at)}
                </span>
              </div>
              <span className="fnb-charge__title">{formatMoney(l.total, l.currency)}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="folio-card__totals">
        <div>
          <span className="fnb-charge__meta">Начислено</span>
          <span className="fnb-charge__title">
            {formatMoney(folio.total_charges, folio.currency)}
          </span>
        </div>
        <div>
          <span className="fnb-charge__meta">Оплачено</span>
          <span className="fnb-charge__title">
            {formatMoney(folio.total_payments, folio.currency)}
          </span>
        </div>
        <div>
          <span className="fnb-charge__meta">К оплате</span>
          <span className="folio-unpaid" data-testid="folio-detail-balance">
            {formatMoney(folio.unpaid, folio.currency)}
          </span>
        </div>
      </div>

      {/* Персонал: действия открываются ОТДЕЛЬНЫМИ экранами (не инлайн-формы) */}
      <div className="folio-form" data-testid="folio-actions">
        <h3 className="fnb-category__title">Действия</h3>
        <div className="folio-form__row">
          <Button onClick={() => onAction('line')} data-testid="folio-add-line-open">
            Добавить позицию
          </Button>
          <Button
            variant="secondary"
            onClick={() => onAction('payment')}
            data-testid="folio-settle-open"
          >
            Записать оплату
          </Button>
        </div>
      </div>

      {/* Персонал: статус фолио */}
      <div className="folio-form" data-testid="folio-status-actions">
        <h3 className="fnb-category__title">Статус счёта</h3>
        <div className="folio-form__row">
          <select
            aria-label="Статус"
            value={folio.status}
            onChange={(e) => setStatus.mutate({ status: e.target.value })}
            data-testid="folio-status-select"
            disabled={setStatus.isPending}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {FOLIO_STATUS_LABELS[s] ?? s}
              </option>
            ))}
          </select>
        </div>
      </div>
    </Card>
  );
}

function FolioRow({
  folio,
  active,
  onSelect,
}: {
  folio: FolioSummary;
  active: boolean;
  onSelect: () => void;
}): JSX.Element {
  return (
    <li>
      <button
        type="button"
        className={`folio-row${active ? ' folio-row--active' : ''}`}
        onClick={onSelect}
        data-testid={`folio-row-${folio.id}`}
      >
        <div className="fnb-charge__main">
          <span className="fnb-charge__title">Счёт {folio.id.slice(0, 8)}</span>
          <span className="fnb-charge__meta">Открыт {formatDateTime(folio.opened_at)}</span>
        </div>
        <div className="folio-row__right">
          <span className={`fnb-charge__state ${statusTone(folio.status)}`}>
            {FOLIO_STATUS_LABELS[folio.status] ?? folio.status}
          </span>
          <span className="folio-unpaid">{formatMoney(folio.unpaid, folio.currency)}</span>
        </div>
      </button>
    </li>
  );
}

export function AllFoliosPage(): JSX.Element {
  const { data, isLoading, isError, refetch } = useFolios(100);
  const [selected, setSelected] = useState<string | undefined>(undefined);
  // Действие над счётом (добавить позицию / записать оплату) открывается
  // ОТДЕЛЬНЫМ ЭКРАНОМ: список и детали не видны, «Назад» возвращает
  // ([[feedback_every_form_is_a_separate_screen]]).
  const [action, setAction] = useState<'line' | 'payment' | null>(null);

  if (action !== null && selected) {
    return action === 'line' ? (
      <FolioAddLineScreen folioId={selected} onBack={() => setAction(null)} />
    ) : (
      <FolioPaymentScreen folioId={selected} onBack={() => setAction(null)} />
    );
  }

  return (
    <section data-testid="all-folios-page">
      <h1 className="hsc-page__title">Все счета</h1>
      <p className="ta-muted">Счета гостей отеля: добавляйте позиции и отмечайте оплату.</p>

      {isLoading ? (
        <ul className="fnb-charges" data-testid="folios-skeleton" aria-busy="true">
          {Array.from({ length: 5 }, (_, i) => (
            <li key={i} className="fnb-charge fnb-charge--skeleton">
              <Skeleton height={16} radius={6} />
            </li>
          ))}
        </ul>
      ) : isError || data === undefined ? (
        <Card data-testid="folios-error">
          <p role="alert">Не удалось загрузить счета.</p>
          <Button variant="secondary" onClick={() => void refetch()}>
            Повторить
          </Button>
        </Card>
      ) : data.items.length === 0 ? (
        <Card data-testid="folios-empty">
          <p>Счетов пока нет.</p>
        </Card>
      ) : (
        <div className="folio-layout">
          <ul className="folio-list folio-list--rows" data-testid="folios-list">
            {data.items.map((f) => (
              <FolioRow
                key={f.id}
                folio={f}
                active={selected === f.id}
                onSelect={() => setSelected(f.id)}
              />
            ))}
          </ul>
          <div className="folio-detail-pane">
            {selected ? (
              <FolioDetailPanel folioId={selected} onAction={setAction} />
            ) : (
              <Card data-testid="folio-detail-empty">
                <p className="ta-muted">
                  Выберите счёт слева, чтобы посмотреть позиции и добавить начисления.
                </p>
              </Card>
            )}
          </div>
        </div>
      )}
    </section>
  );
}


/* ---- ОТДЕЛЬНЫЙ ЭКРАН: добавить позицию в счёт ---- */
function FolioAddLineScreen({
  folioId,
  onBack,
}: {
  folioId: string;
  onBack: () => void;
}): JSX.Element {
  const addLine = useAddFolioLine(folioId);
  const [kind, setKind] = useState<FolioLineKind>('food');
  const [description, setDescription] = useState('');
  const [qty, setQty] = useState('1');
  const [unitPrice, setUnitPrice] = useState('');

  function submit(e: React.FormEvent): void {
    e.preventDefault();
    const q = Number(qty) || 1;
    const price = Number(unitPrice);
    if (!description.trim() || !(price > 0)) return;
    addLine.mutate(
      { kind, description: description.trim(), qty: q, unit_price: price },
      { onSuccess: onBack },
    );
  }

  return (
    <section data-testid="folio-add-line-page">
      <h1 className="hsc-page__title">Добавить позицию</h1>
      <Card>
        <form className="folio-form" onSubmit={submit} data-testid="folio-add-line">
          <div className="folio-form__row">
            <select
              aria-label="Вид"
              value={kind}
              onChange={(e) => setKind(e.target.value as FolioLineKind)}
              data-testid="folio-kind"
            >
              {KIND_OPTIONS.map((k) => (
                <option key={k} value={k}>
                  {FOLIO_KIND_LABELS[k] ?? k}
                </option>
              ))}
            </select>
            <Input
              label="Описание"
              placeholder="напр. рум-сервис: ужин"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              data-testid="folio-desc"
            />
          </div>
          <div className="folio-form__row">
            <Input
              label="Кол-во"
              type="number"
              min="1"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              data-testid="folio-qty"
            />
            <Input
              label="Цена за ед."
              type="number"
              min="0"
              step="0.01"
              value={unitPrice}
              onChange={(e) => setUnitPrice(e.target.value)}
              data-testid="folio-price"
            />
          </div>
          {addLine.isError && (
            <p role="alert" className="ta-muted">
              Не удалось добавить позицию.
            </p>
          )}
          <div className="room-edit__footer">
            <Button type="submit" disabled={addLine.isPending} data-testid="folio-add-submit">
              {addLine.isPending ? 'Добавляем…' : 'Добавить'}
            </Button>
            <Button type="button" variant="secondary" onClick={onBack} data-testid="folio-add-back">
              Назад
            </Button>
          </div>
        </form>
      </Card>
    </section>
  );
}

/* ---- ОТДЕЛЬНЫЙ ЭКРАН: записать оплату по счёту ---- */
function FolioPaymentScreen({
  folioId,
  onBack,
}: {
  folioId: string;
  onBack: () => void;
}): JSX.Element {
  const { data: folio } = useFolioDetail(folioId);
  const settle = useRecordSettlement(folioId);
  const [payAmount, setPayAmount] = useState('');

  function submit(e: React.FormEvent): void {
    e.preventDefault();
    const amount = Number(payAmount);
    if (!(amount > 0)) return;
    settle.mutate({ amount }, { onSuccess: onBack });
  }

  return (
    <section data-testid="folio-payment-page">
      <h1 className="hsc-page__title">Записать оплату</h1>
      <Card>
        <form className="folio-form" onSubmit={submit} data-testid="folio-settle">
          <div className="folio-form__row">
            <Input
              label="Сумма оплаты"
              type="number"
              min="0"
              step="0.01"
              value={payAmount}
              onChange={(e) => setPayAmount(e.target.value)}
              data-testid="folio-pay-amount"
            />
            {folio && (
              <Button
                type="button"
                variant="secondary"
                onClick={() => setPayAmount(String(folio.unpaid))}
                data-testid="folio-pay-full"
                disabled={folio.unpaid <= 0}
              >
                Весь остаток
              </Button>
            )}
          </div>
          {settle.isError && (
            <p role="alert" className="ta-muted">
              Не удалось записать оплату.
            </p>
          )}
          <div className="room-edit__footer">
            <Button type="submit" disabled={settle.isPending} data-testid="folio-settle-submit">
              {settle.isPending ? 'Сохраняем…' : 'Отметить оплату'}
            </Button>
            <Button type="button" variant="secondary" onClick={onBack} data-testid="folio-settle-back">
              Назад
            </Button>
          </div>
        </form>
      </Card>
    </section>
  );
}
